// Blue + Slate corporate HRM palette.
// Light theme is the default and only reachable theme (activeTheme locked to "blue",
// customizer hidden via settings). Dark keys are redefined to the same token family for
// code hygiene but remain untuned and unreachable.

const lightText = {
  primary: "#0F172A",
  secondary: "#475569",
  hint: "#94A3B8",
  disabled: "#94A3B8",
};

const lightPalette = {
  primary: {
    main: "#4F46E5",
    light: "#6366F1",
    dark: "#4338CA",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#334155",
    light: "#475569",
    dark: "#1E293B",
    contrastText: "#FFFFFF",
  },
  background: {
    paper: "#FFFFFF",
    default: "#F8FAFC",
  },
  text: lightText,
  divider: "#E2E8F0",
  success: { main: "#16A34A" },
  warning: { main: "#D97706" },
  error: { main: "#DC2626" },
  info: { main: "#2563EB" },
  action: {
    hover: "#F1F5F9",
    selected: "#E0E7FF",
  },
};

const darkText = {
  primary: "#F8FAFC",
  secondary: "#CBD5E1",
  hint: "#94A3B8",
  disabled: "#94A3B8",
};

const darkPalette = {
  primary: {
    main: "#6366F1",
    light: "#818CF8",
    dark: "#4F46E5",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#94A3B8",
    light: "#CBD5E1",
    dark: "#64748B",
    contrastText: "#0F172A",
  },
  background: {
    paper: "#1E293B",
    default: "#0F172A",
  },
  text: darkText,
  divider: "#334155",
  success: { main: "#22C55E" },
  warning: { main: "#F59E0B" },
  error: { main: "#EF4444" },
  info: { main: "#3B82F6" },
  action: {
    hover: "rgba(148, 163, 184, 0.12)",
    selected: "rgba(99, 102, 241, 0.24)",
  },
};

const lightTheme = { type: "light", ...lightPalette };
const darkTheme = { type: "dark", ...darkPalette };

const darkAppOverrides = {
  sidenavBg: "#1E293B",
  shadow: {
    card: "0 1px 2px rgba(0,0,0,0.40), 0 2px 6px rgba(0,0,0,0.45)",
    cardHover: "0 6px 16px rgba(0,0,0,0.50)",
    popover: "0 10px 28px rgba(0,0,0,0.55)",
    dialog: "0 16px 48px rgba(0,0,0,0.60)",
  },
};

const lightEntry = { palette: lightTheme };
const darkEntry = { palette: darkTheme, app: darkAppOverrides };

export const themeColors = {
  whitePurple: lightEntry,
  whiteBlue: lightEntry,
  slateDark1: darkEntry,
  slateDark2: darkEntry,
  purple1: lightEntry,
  purple2: lightEntry,
  purpleDark1: darkEntry,
  purpleDark2: darkEntry,
  blue: lightEntry,
  blueDark: darkEntry,
  red: darkEntry,
};

export const themeShadows = [
  "none",
  "0px 2px 1px -1px rgba(0, 0, 0, 0.06),0px 1px 1px 0px rgba(0, 0, 0, 0.042),0px 1px 3px 0px rgba(0, 0, 0, 0.036)",
  "0px 3px 1px -2px rgba(0, 0, 0, 0.06),0px 2px 2px 0px rgba(0, 0, 0, 0.042),0px 1px 5px 0px rgba(0, 0, 0, 0.036)",
  "0px 3px 3px -2px rgba(0, 0, 0, 0.06),0px 3px 4px 0px rgba(0, 0, 0, 0.042),0px 1px 8px 0px rgba(0, 0, 0, 0.036)",
  "0px 2px 4px -1px rgba(0, 0, 0, 0.06),0px 4px 5px 0px rgba(0, 0, 0, 0.042),0px 1px 10px 0px rgba(0, 0, 0, 0.036)",
  "0px 3px 5px -1px rgba(0, 0, 0, 0.06),0px 5px 8px 0px rgba(0, 0, 0, 0.042),0px 1px 14px 0px rgba(0, 0, 0, 0.036)",
  "0px 3px 5px -1px rgba(0, 0, 0, 0.06),0px 6px 10px 0px rgba(0, 0, 0, 0.042),0px 1px 18px 0px rgba(0, 0, 0, 0.036)",
  "0px 4px 5px -2px rgba(0, 0, 0, 0.06),0px 7px 10px 1px rgba(0, 0, 0, 0.042),0px 2px 16px 1px rgba(0, 0, 0, 0.036)",
  "0px 5px 5px -3px rgba(0, 0, 0, 0.06),0px 8px 10px 1px rgba(0, 0, 0, 0.042),0px 3px 14px 2px rgba(0, 0, 0, 0.036)",
  "0px 5px 6px -3px rgba(0, 0, 0, 0.06),0px 9px 12px 1px rgba(0, 0, 0, 0.042),0px 3px 16px 2px rgba(0, 0, 0, 0.036)",
  "0px 6px 6px -3px rgba(0, 0, 0, 0.06),0px 10px 14px 1px rgba(0, 0, 0, 0.042),0px 4px 18px 3px rgba(0, 0, 0, 0.036)",
  "0px 6px 7px -4px rgba(0, 0, 0, 0.06),0px 11px 15px 1px rgba(0, 0, 0, 0.042),0px 4px 20px 3px rgba(0, 0, 0, 0.036)",
  "0px 7px 8px -4px rgba(0, 0, 0, 0.06),0px 12px 17px 2px rgba(0, 0, 0, 0.042),0px 5px 22px 4px rgba(0, 0, 0, 0.036)",
  "0px 7px 8px -4px rgba(0, 0, 0, 0.06),0px 13px 19px 2px rgba(0, 0, 0, 0.042),0px 5px 24px 4px rgba(0, 0, 0, 0.036)",
  "0px 7px 9px -4px rgba(0, 0, 0, 0.06),0px 14px 21px 2px rgba(0, 0, 0, 0.042),0px 5px 26px 4px rgba(0, 0, 0, 0.036)",
  "0px 8px 9px -5px rgba(0, 0, 0, 0.06),0px 15px 22px 2px rgba(0, 0, 0, 0.042),0px 6px 28px 5px rgba(0, 0, 0, 0.036)",
  "0px 8px 10px -5px rgba(0, 0, 0, 0.06),0px 16px 24px 2px rgba(0, 0, 0, 0.042),0px 6px 30px 5px rgba(0, 0, 0, 0.036)",
  "0px 8px 11px -5px rgba(0, 0, 0, 0.06),0px 17px 26px 2px rgba(0, 0, 0, 0.042),0px 6px 32px 5px rgba(0, 0, 0, 0.036)",
  "0px 9px 11px -5px rgba(0, 0, 0, 0.06),0px 18px 28px 2px rgba(0, 0, 0, 0.042),0px 7px 34px 6px rgba(0, 0, 0, 0.036)",
  "0px 9px 12px -6px rgba(0, 0, 0, 0.06),0px 19px 29px 2px rgba(0, 0, 0, 0.042),0px 7px 36px 6px rgba(0, 0, 0, 0.036)",
  "0px 10px 13px -6px rgba(0, 0, 0, 0.06),0px 20px 31px 3px rgba(0, 0, 0, 0.042),0px 8px 38px 7px rgba(0, 0, 0, 0.036)",
  "0px 10px 13px -6px rgba(0, 0, 0, 0.06),0px 20px 31px 3px rgba(0, 0, 0, 0.042),0px 8px 38px 7px rgba(0, 0, 0, 0.036)",
  "0px 10px 13px -6px rgba(0, 0, 0, 0.06),0px 20px 31px 3px rgba(0, 0, 0, 0.042),0px 8px 38px 7px rgba(0, 0, 0, 0.036)",
  "0px 10px 13px -6px rgba(0, 0, 0, 0.06),0px 20px 31px 3px rgba(0, 0, 0, 0.042),0px 8px 38px 7px rgba(0, 0, 0, 0.036)",
  "0px 10px 13px -6px rgba(0, 0, 0, 0.06),0px 20px 31px 3px rgba(0, 0, 0, 0.042),0px 8px 38px 7px rgba(0, 0, 0, 0.036)",
];
